import React from 'react';

import { AvailabilityBadge } from '../AvailabilityBadge';
import { Box } from '../Box';
import { Divider } from '../Divider';
import { Icon } from '../Icon';
import { OverflowTooltipText } from '../OverflowTooltipText';
import PackageCardFeatureChecks from '../PackageCard/components/PackageCardFeatureChecks';
import PackageCardMedia from '../PackageCard/components/PackageCardMedia';
import PackageCardSkeleton from '../PackageCard/components/PackageCardSkeleton';
import { Text } from '../Typography';
import {
  cardStyles,
  matchBadgeStyles,
  mediaWrapperStyles,
} from './AIPackageCard.styles';
import type { AIPackageCardProps } from './AIPackageCard.types';
import ExpandableInlineText from './ExpandableInlineText';

const formatReviewCount = (reviewCount: number) =>
  `${reviewCount} Review${reviewCount === 1 ? '' : 's'}`;

const AIPackageCard = ({
  images,
  imageComponent,
  imageSizes,
  title,
  subtitle,
  startingPrice,
  matchPercentage,
  matchBadgeLabel,
  rating,
  reviewCount,
  featureChecks,
  description,
  ctaLabel,
  availabilityBadges,
  isLoading = false,
  className,
  id,
  onClick,
}: AIPackageCardProps) => {
  const isStringDescription = typeof description === 'string';

  if (isLoading) {
    return (
      <PackageCardSkeleton
        className={className}
        hasContentBackground={false}
        orientation="vertical"
      />
    );
  }

  const carouselId =
    id || `ai-package-card-${title.replace(/\s+/g, '-') || 'item'}`;
  const resolvedMatchBadgeLabel =
    matchBadgeLabel ??
    (typeof matchPercentage === 'number'
      ? `${Math.round(matchPercentage)}% Match`
      : undefined);
  const shouldShowReviewSummary =
    typeof rating === 'number' &&
    typeof reviewCount === 'number' &&
    reviewCount > 0;
  const shouldShowDivider = !!description || !!ctaLabel;

  return (
    <Box css={cardStyles(!!onClick)} className={className} onClick={onClick}>
      <Box css={mediaWrapperStyles}>
        <PackageCardMedia
          carouselId={carouselId}
          images={images}
          imageComponent={imageComponent}
          imageSizes={imageSizes}
          carouselLabel={`${title} image gallery`}
          availabilityBadges={availabilityBadges}
          orientation="vertical"
        />

        {resolvedMatchBadgeLabel && (
          <Box css={matchBadgeStyles}>
            <AvailabilityBadge variant="success">
              {resolvedMatchBadgeLabel}
            </AvailabilityBadge>
          </Box>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
        <Box display="flex" flexDirection="column" gap="var(--spacing-1)">
          <OverflowTooltipText
            text={title}
            lines={3}
            size="sm"
            fontWeight="bold"
          />
          <Text size="xs" fontWeight="bold">
            {subtitle}
          </Text>
        </Box>

        {shouldShowReviewSummary && (
          <Box
            display="flex"
            alignItems="center"
            ml="-2px"
            gap="var(--spacing-05)"
          >
            <Icon
              variant="StarSolid"
              size="medium"
              fill="var(--surface-action-2)"
            />
            <Text size="xs" fontWeight="semibold">
              {rating.toFixed(1)} {' • '} {formatReviewCount(reviewCount)}
            </Text>
          </Box>
        )}

        {startingPrice && (
          <Text size="xs" fontWeight="normal">
            Starting Price {startingPrice} / Guest
          </Text>
        )}

        {featureChecks && (
          <PackageCardFeatureChecks featureChecks={featureChecks} />
        )}

        {shouldShowDivider && <Divider my="var(--spacing-0)" />}

        {description &&
          (isStringDescription ? (
            <ExpandableInlineText text={description} size="xs" />
          ) : (
            <Text size="xs">{description}</Text>
          ))}

        {ctaLabel && (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="var(--spacing-1)"
            css={{
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            <Text as="span" size="xs" fontWeight="semibold">
              {ctaLabel} {'\u2192'}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AIPackageCard;
